import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import attendanceService from '../services/attendanceService';
import schoolsService from '../services/schoolsService';
import classesService from '../services/classesService';
import studentsService from '../services/studentsService';
import AttendanceAlertSMSModal from '../components/communication/AttendanceAlertSMSModal';
import { Phone, MagnifyingGlass } from '@phosphor-icons/react';

function AttendancePage() {
  const { user } = useAuth();
  const isAdmin = user && ['super_admin', 'school_admin'].includes(user.role);

  const [classes, setClasses] = useState([]);
  const [academicTerms, setAcademicTerms] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('');
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendance, setAttendance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSMSModal, setShowSMSModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [alertAttendanceRate, setAlertAttendanceRate] = useState(0);
  const [loadingAlertRate, setLoadingAlertRate] = useState(false);

  // Admin overview state
  const [overview, setOverview] = useState(null);

  // Quick-absent state (admin only)
  const [absentSearch, setAbsentSearch] = useState('');
  const [absentResults, setAbsentResults] = useState([]);
  const [absentSearching, setAbsentSearching] = useState(false);
  const [markingAbsent, setMarkingAbsent] = useState(null);

  useEffect(() => {
    classesService.listClasses()
      .then(data => setClasses(data || []))
      .catch(err => console.error(err));

    schoolsService.listAllTerms()
      .then(data => {
        const items = data.items || data || [];
        setAcademicTerms(items);
        if (items.length > 0) setSelectedTerm(items[0].id);
      })
      .catch(err => console.error(err));

    if (isAdmin) {
      api.get('/api/dashboard/overview')
        .then(data => setOverview(data?.stats))
        .catch(() => {});
    }
  }, [isAdmin]);

  const loadAttendance = useCallback(() => {
    if (!selectedClass) return;
    setLoading(true);
    attendanceService.getClassAttendance(selectedClass, attendanceDate)
      .then(data => setAttendance(data))
      .catch(err => toast.error('Failed to load attendance'))
      .finally(() => setLoading(false));
  }, [selectedClass, attendanceDate]);

  useEffect(() => {
    if (selectedClass) loadAttendance();
  }, [selectedClass, attendanceDate, loadAttendance]);

  const markAttendance = async (record, status) => {
    if (!selectedTerm) {
      toast.error('Please select an academic term');
      return;
    }
    try {
      if (record.recorded && record.attendance_id) {
        // Already has a record for this date — update it instead of creating a duplicate
        await attendanceService.updateAttendance(record.attendance_id, status);
      } else {
        await attendanceService.markAttendance({
          student_id: record.student_id,
          class_id: selectedClass,
          academic_term_id: selectedTerm,
          attendance_date: attendanceDate,
          status
        });
      }
      toast.success('Attendance marked');
      loadAttendance();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Fetch the student's real attendance rate before opening the SMS alert —
  // this used to always show/send a hardcoded 75% for every student.
  const handleOpenSMSAlert = async (record) => {
    setSelectedStudent({
      id: record.student_id,
      first_name: record.student_name.split(' ')[0],
      last_name: record.student_name.split(' ').slice(1).join(' '),
      parent_name: record.parent_name,
      parent_phone: record.parent_phone
    });
    setLoadingAlertRate(true);
    try {
      const data = await attendanceService.getStudentAttendance(record.student_id);
      setAlertAttendanceRate(data?.summary?.attendance_percentage ?? 0);
    } catch (error) {
      toast.error('Could not load attendance rate');
      setAlertAttendanceRate(0);
    } finally {
      setLoadingAlertRate(false);
      setShowSMSModal(true);
    }
  };

  // Admin quick-absent: search students
  const handleAbsentSearch = useCallback(async () => {
    if (!absentSearch.trim() || absentSearch.length < 2) return;
    setAbsentSearching(true);
    try {
      const data = await studentsService.listStudents({ search: absentSearch });
      setAbsentResults(data.items || []);
    } catch {
      toast.error('Search failed');
    } finally {
      setAbsentSearching(false);
    }
  }, [absentSearch]);

  useEffect(() => {
    if (absentSearch.length >= 2) {
      const t = setTimeout(handleAbsentSearch, 350);
      return () => clearTimeout(t);
    } else {
      setAbsentResults([]);
    }
  }, [absentSearch, handleAbsentSearch]);

  const handleQuickAbsent = async (student) => {
    if (!student.class_id) {
      toast.error('Student has no class assigned');
      return;
    }
    if (!selectedTerm) {
      toast.error('Please select a term first');
      return;
    }
    setMarkingAbsent(student.id);
    try {
      await attendanceService.markAttendance({
        student_id: student.id,
        class_id: student.class_id,
        academic_term_id: selectedTerm,
        attendance_date: attendanceDate,
        status: 'absent'
      });
      toast.success(`${student.full_name} marked absent for ${attendanceDate}`);
      setAbsentSearch('');
      setAbsentResults([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.detail || 'Failed to mark absent');
    } finally {
      setMarkingAbsent(null);
    }
  };

  return (
    <Layout>
      <div className="page" data-testid="attendance-page">
        <div className="page-header">
          <div>
            <h1>Attendance</h1>
            <p>Mark and track daily attendance</p>
          </div>
        </div>

        {/* Admin overview banner */}
        {isAdmin && overview && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
            {[
              { label: "Today's Present", value: overview.attendance_today?.present ?? '—', color: '#10b981' },
              { label: "Today's Absent", value: overview.attendance_today?.absent ?? '—', color: '#ef4444' },
              { label: 'Attendance Rate', value: `${overview.attendance_today?.attendance_rate ?? 0}%`, color: '#3b82f6' },
              { label: 'Total Students', value: overview.total_students ?? '—', color: '#6366f1' },
            ].map(({ label, value, color }) => (
              <div key={label} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 18px' }}>
                <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Admin quick-absent panel */}
        {isAdmin && (
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, marginBottom: 24 }}>
            <h3 style={{ margin: '0 0 10px 0', fontSize: 15, fontWeight: 600 }}>Mark Student Absent (Admin)</h3>
            <p style={{ margin: '0 0 12px 0', fontSize: 13, color: '#6b7280' }}>
              For students who reported sick or requested a day off at administration.
            </p>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <div style={{ position: 'relative', flex: 1, maxWidth: 360 }}>
                <MagnifyingGlass size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                <input
                  type="text"
                  value={absentSearch}
                  onChange={e => setAbsentSearch(e.target.value)}
                  placeholder="Search student by name or ID..."
                  style={{ width: '100%', paddingLeft: 34, paddingRight: 10, paddingTop: 8, paddingBottom: 8, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
                />
              </div>
              <input
                type="date"
                value={attendanceDate}
                onChange={e => setAttendanceDate(e.target.value)}
                style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
              />
            </div>
            {absentSearching && <div style={{ fontSize: 13, color: '#6b7280' }}>Searching...</div>}
            {absentResults.length > 0 && (
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', marginTop: 8 }}>
                {absentResults.map(stu => (
                  <div key={stu.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid #f3f4f6' }}>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 14 }}>{stu.full_name}</div>
                      <div style={{ fontSize: 12, color: '#9ca3af' }}>{stu.student_id} · {stu.class_name || 'No class'}</div>
                    </div>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleQuickAbsent(stu)}
                      disabled={markingAbsent === stu.id}
                    >
                      {markingAbsent === stu.id ? 'Marking...' : 'Mark Absent'}
                    </button>
                  </div>
                ))}
              </div>
            )}
            {absentSearch.length >= 2 && !absentSearching && absentResults.length === 0 && (
              <div style={{ fontSize: 13, color: '#9ca3af', marginTop: 8 }}>No students found</div>
            )}
          </div>
        )}

        {/* Per-class attendance */}
        <div className="attendance-controls">
          <div className="form-group">
            <label>Select Class</label>
            <select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              data-testid="class-select"
            >
              <option value="">Choose a class...</option>
              {classes.map(cls => (
                <option key={cls.id} value={cls.id}>{cls.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Academic Term</label>
            <select
              value={selectedTerm}
              onChange={(e) => setSelectedTerm(e.target.value)}
              data-testid="term-select"
            >
              <option value="">Choose a term...</option>
              {academicTerms.map(term => (
                <option key={term.id} value={term.id}>
                  {term.academic_year} - {term.term}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Date</label>
            <input
              type="date"
              value={attendanceDate}
              onChange={(e) => setAttendanceDate(e.target.value)}
              data-testid="date-input"
            />
          </div>
        </div>

        {attendance && (
          <div className="attendance-summary">
            <div className="summary-stat present">
              <span className="stat-value">{attendance.summary?.present || 0}</span>
              <span className="stat-label">Present</span>
            </div>
            <div className="summary-stat absent">
              <span className="stat-value">{attendance.summary?.absent || 0}</span>
              <span className="stat-label">Absent</span>
            </div>
            <div className="summary-stat late">
              <span className="stat-value">{attendance.summary?.late || 0}</span>
              <span className="stat-label">Late</span>
            </div>
            <div className="summary-stat not-recorded">
              <span className="stat-value">{attendance.summary?.not_recorded || 0}</span>
              <span className="stat-label">Not Recorded</span>
            </div>
          </div>
        )}

        {loading ? (
          <div className="loading">Loading attendance...</div>
        ) : attendance?.records ? (
          <div className="table-container">
            <table className="data-table" data-testid="attendance-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {attendance.records.map(record => (
                  <tr key={record.student_id} data-testid={`attendance-row-${record.student_id}`}>
                    <td>{record.student_name}</td>
                    <td>
                      {record.recorded ? (
                        <span className={`badge ${record.status}`}>{record.status}</span>
                      ) : (
                        <span className="badge not-recorded">Not Recorded</span>
                      )}
                    </td>
                    <td>
                      <div className="attendance-buttons">
                        <button
                          className={`btn btn-sm ${record.status === 'present' ? 'btn-success' : 'btn-outline'}`}
                          onClick={() => markAttendance(record, 'present')}
                          data-testid={`mark-present-${record.student_id}`}
                        >
                          Present
                        </button>
                        <button
                          className={`btn btn-sm ${record.status === 'absent' ? 'btn-danger' : 'btn-outline'}`}
                          onClick={() => markAttendance(record, 'absent')}
                          data-testid={`mark-absent-${record.student_id}`}
                        >
                          Absent
                        </button>
                        <button
                          className={`btn btn-sm ${record.status === 'late' ? 'btn-warning' : 'btn-outline'}`}
                          onClick={() => markAttendance(record, 'late')}
                          data-testid={`mark-late-${record.student_id}`}
                        >
                          Late
                        </button>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleOpenSMSAlert(record)}
                          disabled={loadingAlertRate}
                          title="Send attendance alert SMS"
                        >
                          <Phone size={14} /> Alert
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : selectedClass ? (
          <div className="empty-state">
            <p>No students in this class</p>
          </div>
        ) : (
          <div className="empty-state">
            <p>Select a class to view attendance</p>
          </div>
        )}
      </div>

      <AttendanceAlertSMSModal
        isOpen={showSMSModal}
        onClose={() => setShowSMSModal(false)}
        student={selectedStudent}
        attendance={alertAttendanceRate}
        onSuccess={() => {}}
      />
    </Layout>
  );
}

export default AttendancePage;
